import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_V1, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trophy, Sparkles, Wand2, Target, Loader2, Trash2, Link as LinkIcon, Paperclip,
  ArrowRight, Calendar as CalendarIcon, Filter, X, Star, Lightbulb, Copy, Check,
  ScrollText, Award, BookOpen, TrendingUp, Users as UsersIcon, ThumbsUp, Heart,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const CATEGORIES = [
  { value: "delivery", label: "Delivery", icon: TrendingUp, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "impact", label: "Impact", icon: Award, color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "learning", label: "Learning", icon: BookOpen, color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "leadership", label: "Leadership", icon: Trophy, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "collaboration", label: "Collaboration", icon: UsersIcon, color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { value: "recognition", label: "Recognition", icon: Heart, color: "bg-rose-50 text-rose-700 border-rose-200" },
  { value: "other", label: "Other", icon: Sparkles, color: "bg-slate-100 text-slate-700 border-slate-200" },
];

const CAT_BY_VAL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

function CategoryChip({ value, size = "sm" }) {
  const c = CAT_BY_VAL[value] || CAT_BY_VAL.other;
  const Icon = c.icon;
  return (
    <span
      data-testid={`win-cat-${value}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium",
        c.color,
        size === "sm" ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1",
      )}
    >
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

function PrideStars({ value, onChange, readOnly }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="pride-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => !readOnly && onChange?.(n)}
          className={cn(
            "p-0.5 rounded transition",
            !readOnly && "hover:scale-110",
          )}
          aria-label={`pride ${n}`}
          data-testid={`pride-star-${n}`}
        >
          <Star
            className={cn(
              "w-4 h-4",
              n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300",
            )}
          />
        </button>
      ))}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 px-2 text-xs"
      onClick={async () => {
        await navigator.clipboard.writeText(text || "");
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      data-testid="copy-narrative-btn"
    >
      {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

export default function WinsJournal() {
  const [wins, setWins] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeGoals, setActiveGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");
  const [filterGoal, setFilterGoal] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createSeed, setCreateSeed] = useState(null);
  const [editing, setEditing] = useState(null); // win object opened for AI tools / edits
  const [exportOpen, setExportOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [w, cov, sug, cy] = await Promise.all([
        api.get("/wins"),
        api.get("/wins/coverage/me"),
        api.get("/wins/suggestions/me"),
        api.get("/cycles/active/me"),
      ]);
      setWins(w.data.items || []);
      setCoverage(cov.data);
      setSuggestions(sug.data.items || []);
      const items = cy.data.items || [];
      const activeCycleId = items?.[0]?.cycle?.id;
      if (activeCycleId) {
        const o = await api.get("/objectives", { params: { cycle_id: activeCycleId } });
        setActiveGoals(o.data.items || []);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load wins");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    return wins.filter((w) => {
      if (filterCat && w.category !== filterCat) return false;
      if (filterGoal && !(w.linked_objective_ids || []).includes(filterGoal)) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(w.title || "").toLowerCase().includes(s) && !(w.description || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [wins, filterCat, filterGoal, search]);

  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((w) => {
      const k = (w.occurred_at || "").slice(0, 7) || "—";
      out[k] = out[k] || [];
      out[k].push(w);
    });
    return Object.entries(out).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filtered]);

  const goalLabel = (id) => {
    const g = activeGoals.find((x) => x.id === id);
    if (!g) return "Goal";
    return (g.fields?.statement || g.fields?.objective || "Goal").slice(0, 80);
  };

  const stats = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    const qStart = start.toISOString().slice(0, 10);
    const thisQ = wins.filter((w) => (w.occurred_at || "") >= qStart).length;
    const goalsCovered = new Set(wins.flatMap((w) => w.linked_objective_ids || [])).size;
    const unlinked = wins.filter((w) => !(w.linked_objective_ids || []).length).length;
    return { total: wins.length, thisQ, goalsCovered, unlinked };
  }, [wins]);

  return (
    <div className="space-y-8 max-w-7xl" data-testid="wins-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">My Wins · achievement journal</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">
            Capture wins as they happen.
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            A private journal of your achievements. We'll help you tie each win to a goal so review
            time becomes a recap, not a recall test.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setExportOpen(true)}
            data-testid="open-export-btn"
            disabled={!wins.length}
          >
            <ScrollText className="w-4 h-4 mr-2" /> Generate review draft
          </Button>
          <Button
            onClick={() => { setCreateSeed(null); setCreateOpen(true); }}
            data-testid="open-create-win-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            <Plus className="w-4 h-4 mr-2" /> Log a win
          </Button>
        </div>
      </div>

      {/* counters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wins-stats">
        <Counter label="Total wins" value={stats.total} icon={Trophy} accent="bg-amber-50 text-amber-700" testid="stat-total" />
        <Counter label="This quarter" value={stats.thisQ} icon={CalendarIcon} accent="bg-violet-50 text-violet-700" testid="stat-quarter" />
        <Counter label="Goals covered" value={stats.goalsCovered} icon={Target} accent="bg-emerald-50 text-emerald-700" testid="stat-goals-covered" />
        <Counter label="Missing goal links" value={stats.unlinked} icon={LinkIcon} accent="bg-rose-50 text-rose-700" testid="stat-unlinked" />
      </div>

      {/* suggestions strip */}
      {suggestions.length > 0 && (
        <section className="axia-card p-5" data-testid="wins-suggestions">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-600" />
              <h2 className="font-display text-base font-semibold text-slate-900">
                Capture these — recently
              </h2>
            </div>
            <span className="text-xs text-slate-400">Auto-detected from your kudos & closed goals</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {suggestions.slice(0, 6).map((s) => (
              <button
                key={`${s.source}-${s.source_ref_id}`}
                onClick={() => {
                  setCreateSeed({
                    title: s.title_suggestion,
                    description: s.description_suggestion,
                    category: s.category_suggestion,
                    occurred_at: s.occurred_at,
                    source: s.source,
                    source_ref_id: s.source_ref_id,
                  });
                  setCreateOpen(true);
                }}
                className="text-left rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 p-3 transition group"
                data-testid={`suggestion-${s.source}-${s.source_ref_id}`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <CategoryChip value={s.category_suggestion} />
                  <span className="text-[11px] text-slate-400 font-mono">{s.occurred_at}</span>
                </div>
                <div className="text-sm font-medium text-slate-900 line-clamp-1">{s.title_suggestion}</div>
                <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{s.description_suggestion}</div>
                <div className="mt-2 inline-flex items-center text-xs text-slate-700 font-medium">
                  Log it <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="flex flex-wrap items-center gap-2" data-testid="wins-filters">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Filter className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search wins…"
            className="pl-9 h-9"
            data-testid="search-input"
          />
        </div>
        <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-44" data-testid="filter-category">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterGoal || "all"} onValueChange={(v) => setFilterGoal(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-72" data-testid="filter-goal">
            <SelectValue placeholder="All goals" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All goals</SelectItem>
            {activeGoals.map((g) => (
              <SelectItem key={g.id} value={g.id}>{goalLabel(g.id)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterCat || filterGoal || search) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterCat(""); setFilterGoal(""); setSearch(""); }} data-testid="clear-filters-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {loading ? (
            <div className="text-sm text-slate-400 text-center py-12">Loading…</div>
          ) : grouped.length === 0 ? (
            <EmptyState onCreate={() => setCreateOpen(true)} />
          ) : (
            grouped.map(([month, items]) => (
              <section key={month}>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2">
                  {formatMonthLabel(month)}
                </div>
                <div className="space-y-3">
                  {items.map((w) => (
                    <WinCard
                      key={w.id}
                      win={w}
                      goalLabel={goalLabel}
                      onOpen={() => setEditing(w)}
                      onDelete={async () => {
                        if (!confirm("Delete this win?")) return;
                        await api.delete(`/wins/${w.id}`);
                        toast.success("Win removed");
                        reload();
                      }}
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </div>

        {/* Goal coverage */}
        <aside className="space-y-4">
          <section className="axia-card p-5" data-testid="goal-coverage-card">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base font-semibold text-slate-900">Goal coverage</h2>
              <Target className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 mt-1">How many wins each active goal has — red = needs attention.</p>
            <div className="mt-4 space-y-3">
              {(coverage?.items || []).length === 0 && (
                <div className="text-sm text-slate-400">No active goals.</div>
              )}
              {(coverage?.items || []).map((g) => (
                <CoverageRow key={g.objective_id} g={g} />
              ))}
              {coverage && coverage.unlinked_wins > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-amber-700 bg-amber-50 rounded-md p-2.5">
                  <strong>{coverage.unlinked_wins}</strong> win{coverage.unlinked_wins > 1 ? "s" : ""} not yet linked to any goal — open a win and tap <span className="font-medium">"Tie to a goal"</span>.
                </div>
              )}
            </div>
          </section>
        </aside>
      </div>

      <CreateWinDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        seed={createSeed}
        activeGoals={activeGoals}
        onCreated={(w) => { reload(); setEditing(w); }}
      />

      <WinDetailDialog
        win={editing}
        onClose={() => setEditing(null)}
        activeGoals={activeGoals}
        goalLabel={goalLabel}
        onChanged={reload}
      />

      <ExportNarrativeDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        wins={wins}
        activeGoals={activeGoals}
      />
    </div>
  );
}

/* ---------------- pieces ---------------- */
function Counter({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="axia-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="font-display text-3xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
        </div>
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center", accent)}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function CoverageRow({ g }) {
  const tone =
    g.thinness === "cold" ? "bg-rose-500"
      : g.thinness === "stale" ? "bg-amber-500"
        : "bg-emerald-500";
  const note =
    g.thinness === "cold" ? "No wins yet"
      : g.thinness === "stale" ? `${g.days_since_last_win}d since last`
        : `${g.days_since_last_win ?? 0}d ago`;
  return (
    <div data-testid={`coverage-row-${g.objective_id}`}>
      <div className="flex items-center justify-between text-xs gap-2">
        <span className="font-medium text-slate-700 line-clamp-1">{g.label}</span>
        <span className="font-mono text-slate-500 shrink-0">{g.win_count}</span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={cn("h-full transition-all", tone)} style={{ width: `${Math.min(100, g.win_count * 20)}%` }} />
        </div>
        <span className="text-[10px] text-slate-400 shrink-0">{note}</span>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="axia-card p-12 text-center" data-testid="wins-empty">
      <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 inline-flex items-center justify-center mb-4">
        <Trophy className="w-6 h-6" />
      </div>
      <div className="font-display text-lg font-semibold text-slate-900">No wins yet</div>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        The best time to capture an achievement is the moment it happens. Even a one-line note now
        will pay back tenfold at review time.
      </p>
      <Button onClick={onCreate} className="mt-5 bg-slate-900 hover:bg-slate-800" data-testid="empty-create-btn">
        <Plus className="w-4 h-4 mr-2" /> Log your first win
      </Button>
    </div>
  );
}

function WinCard({ win, goalLabel, onOpen, onDelete }) {
  const linked = win.linked_objective_ids || [];
  return (
    <div
      onClick={onOpen}
      className="axia-card p-4 hover:border-slate-300 cursor-pointer transition group"
      data-testid={`win-card-${win.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex flex-col items-center pt-1 shrink-0">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Trophy className="w-4 h-4" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryChip value={win.category} />
            <span className="text-[11px] text-slate-400 font-mono">{win.occurred_at}</span>
            <PrideStars value={win.pride_level} readOnly />
          </div>
          <div className="font-display text-base font-semibold text-slate-900 mt-1.5 line-clamp-1">
            {win.title}
          </div>
          {win.description && (
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{win.description}</p>
          )}
          <div className="flex items-center gap-3 mt-3">
            {linked.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1">
                {linked.slice(0, 2).map((oid) => (
                  <span
                    key={oid}
                    className="inline-flex items-center gap-1 text-[11px] text-slate-700 bg-slate-100 rounded px-2 py-0.5 max-w-[220px]"
                    data-testid={`win-goal-${win.id}-${oid}`}
                  >
                    <Target className="w-3 h-3 shrink-0" />
                    <span className="truncate">{goalLabel(oid)}</span>
                  </span>
                ))}
                {linked.length > 2 && (
                  <span className="text-[11px] text-slate-400">+{linked.length - 2} more</span>
                )}
              </div>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-0.5">
                <LinkIcon className="w-3 h-3" /> not tied to a goal yet
              </span>
            )}
            {(win.evidence || []).length > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <Paperclip className="w-3 h-3" /> {win.evidence.length}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-rose-600 p-1.5"
          aria-label="delete"
          data-testid={`delete-win-${win.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ---------------- Create Dialog ---------------- */
function CreateWinDialog({ open, onOpenChange, seed, activeGoals, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("delivery");
  const [pride, setPride] = useState(3);
  const [occurredAt, setOccurredAt] = useState("");
  const [linked, setLinked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(seed?.title || "");
      setDescription(seed?.description || "");
      setCategory(seed?.category || "delivery");
      setPride(3);
      setOccurredAt(seed?.occurred_at || new Date().toISOString().slice(0, 10));
      setLinked([]);
    }
  }, [open, seed]);

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Give it a short title");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/wins", {
        title: title.trim(),
        description: description.trim(),
        category,
        pride_level: pride,
        occurred_at: occurredAt,
        linked_objective_ids: linked,
        source: seed?.source || "manual",
        source_ref_id: seed?.source_ref_id || null,
      });
      toast.success("Win logged");
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save win");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="create-win-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-600" /> Log a win</DialogTitle>
          <DialogDescription>Tiny notes pay big dividends. You can add evidence and tie it to a goal after saving.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Closed enterprise Acme deal worth 3 logos"
              maxLength={180}
              data-testid="win-title-input"
            />
          </div>
          <div>
            <Label className="text-xs">What happened? <span className="text-slate-400 font-normal">(situation, action, outcome)</span></Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="A few sentences is plenty. Be specific — dates, numbers, names of customers/projects."
              data-testid="win-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="win-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} data-testid="win-date-input" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Pride level <span className="text-slate-400 font-normal">— for you only</span></Label>
            <div className="mt-1"><PrideStars value={pride} onChange={setPride} /></div>
          </div>
          {activeGoals.length > 0 && (
            <div>
              <Label className="text-xs">Tie to goals <span className="text-slate-400 font-normal">(optional — AI can suggest later)</span></Label>
              <div className="mt-1.5 space-y-1.5 max-h-40 overflow-y-auto rounded-md border border-slate-200 p-2">
                {activeGoals.map((g) => {
                  const checked = linked.includes(g.id);
                  return (
                    <label
                      key={g.id}
                      className="flex items-start gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1.5 py-1 rounded"
                      data-testid={`link-goal-${g.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setLinked((prev) => e.target.checked ? [...prev, g.id] : prev.filter((id) => id !== g.id))}
                        className="mt-0.5"
                      />
                      <span className="text-slate-700 line-clamp-2">{g.fields?.statement || g.fields?.objective || "Goal"}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-create-win">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-win-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Save win
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Detail Dialog (AI tools, evidence, edit) ---------------- */
function WinDetailDialog({ win, onClose, activeGoals, goalLabel, onChanged }) {
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [strengthening, setStrengthening] = useState(false);
  const [strong, setStrong] = useState(null);
  const [adding, setAdding] = useState(false);
  const [evUrl, setEvUrl] = useState({ title: "", url: "" });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setAiSuggestions(null);
    setStrong(null);
    setEvUrl({ title: "", url: "" });
  }, [win?.id]);

  if (!win) return null;

  const linked = win.linked_objective_ids || [];

  const suggest = async () => {
    setAiSuggesting(true);
    try {
      const r = await api.post(`/wins/${win.id}/suggest-goal-links`);
      setAiSuggestions(r.data);
      if (!r.data.matches?.length) toast.info(r.data.message || "No clear matches found");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setAiSuggesting(false);
    }
  };

  const accept = async (oid) => {
    try {
      await api.post(`/wins/${win.id}/accept-suggestion/${oid}`);
      toast.success("Linked to goal");
      onChanged?.();
      // close so it reloads from list — better UX than mutating local state silently
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    }
  };

  const strengthen = async () => {
    setStrengthening(true);
    try {
      const r = await api.post(`/wins/${win.id}/strengthen`);
      setStrong(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setStrengthening(false);
    }
  };

  const applyStrong = async () => {
    if (!strong?.rewritten) return;
    try {
      await api.patch(`/wins/${win.id}`, { description: strong.rewritten });
      toast.success("Description updated");
      onChanged?.();
      setStrong(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const addUrlEvidence = async () => {
    if (!evUrl.title || !evUrl.url) return;
    setAdding(true);
    try {
      await api.post(`/wins/${win.id}/evidence/url`, evUrl);
      toast.success("Link added");
      setEvUrl({ title: "", url: "" });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setAdding(false);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", file.name);
      fd.append("file", file);
      await fetch(`${API_V1}/wins/${win.id}/evidence/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        body: fd,
      }).then((r) => { if (!r.ok) throw new Error("upload failed"); });
      toast.success("File attached");
      onChanged?.();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeEvidence = async (eid) => {
    await api.delete(`/wins/${win.id}/evidence/${eid}`);
    onChanged?.();
  };

  return (
    <Dialog open={!!win} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="win-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryChip value={win.category} />
            <span className="text-[11px] text-slate-400 font-mono">{win.occurred_at}</span>
            <PrideStars value={win.pride_level} readOnly />
          </div>
          <DialogTitle className="font-display text-xl">{win.title}</DialogTitle>
          {win.description && (
            <DialogDescription className="text-slate-700 leading-relaxed whitespace-pre-wrap">{win.description}</DialogDescription>
          )}
        </DialogHeader>

        {/* Linked goals + AI suggest */}
        <section className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Linked goals</div>
            <Button size="sm" variant="outline" onClick={suggest} disabled={aiSuggesting} data-testid="suggest-goals-btn">
              {aiSuggesting ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Sparkles className="w-3 h-3 mr-2" />}
              Tie to a goal (AI)
            </Button>
          </div>
          {linked.length === 0 && !aiSuggestions && (
            <div className="text-xs text-slate-400">No goals linked yet.</div>
          )}
          {linked.length > 0 && (
            <ul className="space-y-1.5">
              {linked.map((oid) => (
                <li key={oid} className="text-sm text-slate-700 flex items-center gap-2">
                  <Target className="w-3.5 h-3.5 text-slate-400" /> {goalLabel(oid)}
                </li>
              ))}
            </ul>
          )}
          {aiSuggestions?.matches?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              <div className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold">AI suggestions</div>
              {aiSuggestions.matches.map((m) => (
                <div key={m.objective_id} className="flex items-start justify-between gap-3 bg-violet-50/40 rounded-md p-2.5 border border-violet-100" data-testid={`ai-suggestion-${m.objective_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 line-clamp-1">{goalLabel(m.objective_id)}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{m.rationale}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => accept(m.objective_id)} data-testid={`accept-suggestion-${m.objective_id}`}>
                    <Check className="w-3 h-3 mr-1" /> Accept
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Strengthen */}
        <section className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Strengthen the narrative</div>
            <Button size="sm" variant="outline" onClick={strengthen} disabled={strengthening} data-testid="strengthen-btn">
              {strengthening ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Wand2 className="w-3 h-3 mr-2" />}
              Rewrite as STAR
            </Button>
          </div>
          {!strong && <div className="text-xs text-slate-400">AI will rewrite your description in STAR form and prompt you for any missing metrics.</div>}
          {strong && (
            <div className="space-y-2">
              <div className="bg-slate-50 rounded-md p-3 text-sm text-slate-800 whitespace-pre-wrap">{strong.rewritten}</div>
              {strong.metric_prompts?.length > 0 && (
                <div className="text-xs text-slate-600">
                  <div className="font-medium mb-1">Add these to make it stronger:</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {strong.metric_prompts.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={() => setStrong(null)}>Dismiss</Button>
                <Button size="sm" onClick={applyStrong} data-testid="apply-strong-btn"><Check className="w-3 h-3 mr-1" /> Replace description</Button>
              </div>
            </div>
          )}
        </section>

        {/* Evidence */}
        <section className="rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">Evidence</div>
          </div>
          {(win.evidence || []).length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {win.evidence.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded-md px-2.5 py-1.5" data-testid={`evidence-row-${e.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    {e.type === "url" ? <LinkIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                    {e.type === "url" ? (
                      <a href={e.url} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-slate-900 truncate">{e.title}</a>
                    ) : (
                      <a href={`${API_V1}/wins/${win.id}/evidence/${e.id}/download`} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-slate-900 truncate">{e.title}</a>
                    )}
                  </div>
                  <button onClick={() => removeEvidence(e.id)} className="text-slate-400 hover:text-rose-600 p-1"><X className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input placeholder="Link title" value={evUrl.title} onChange={(e) => setEvUrl((p) => ({ ...p, title: e.target.value }))} className="h-8 text-sm" data-testid="evidence-link-title" />
            <Input placeholder="https://…" value={evUrl.url} onChange={(e) => setEvUrl((p) => ({ ...p, url: e.target.value }))} className="h-8 text-sm" data-testid="evidence-link-url" />
            <Button size="sm" variant="outline" disabled={adding || !evUrl.title || !evUrl.url} onClick={addUrlEvidence} data-testid="add-link-evidence-btn">
              <LinkIcon className="w-3 h-3 mr-1" /> Add link
            </Button>
          </div>
          <div className="mt-2">
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
              <Paperclip className="w-3.5 h-3.5" />
              {uploading ? "Uploading…" : "Attach a file (≤25 MB)"}
              <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} data-testid="upload-file-input" />
            </label>
          </div>
        </section>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-detail-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Export Narrative Dialog ---------------- */
function ExportNarrativeDialog({ open, onOpenChange, wins, activeGoals }) {
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [roleKind, setRoleKind] = useState("self");

  useEffect(() => {
    if (open) {
      const init = {};
      wins.forEach((w) => { init[w.id] = true; });
      setSelected(init);
      setNarrative("");
    }
  }, [open, wins]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const run = async () => {
    if (!selectedIds.length) return toast.error("Pick at least one win");
    setRunning(true);
    try {
      const r = await api.post("/wins/export-narrative", { win_ids: selectedIds, role_kind: roleKind });
      setNarrative(r.data.narrative || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="export-narrative-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScrollText className="w-4 h-4" /> Generate review draft</DialogTitle>
          <DialogDescription>Pick the wins to include — we'll group them by goal and write a draft you can paste into your review.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 border border-slate-200 rounded-lg p-3 max-h-72 overflow-y-auto space-y-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Wins ({selectedIds.length}/{wins.length})</span>
              <button
                className="text-xs text-slate-500 hover:text-slate-900"
                onClick={() => {
                  const all = selectedIds.length === wins.length;
                  const next = {};
                  wins.forEach((w) => { next[w.id] = !all; });
                  setSelected(next);
                }}
                data-testid="toggle-all-btn"
              >{selectedIds.length === wins.length ? "Clear" : "Select all"}</button>
            </div>
            {wins.map((w) => (
              <label key={w.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer" data-testid={`export-pick-${w.id}`}>
                <input type="checkbox" checked={!!selected[w.id]} onChange={(e) => setSelected((p) => ({ ...p, [w.id]: e.target.checked }))} className="mt-0.5" />
                <span className="text-xs text-slate-700 line-clamp-2">{w.title}</span>
              </label>
            ))}
          </div>
          <div className="lg:col-span-3 space-y-3">
            <div>
              <Label className="text-xs">Perspective</Label>
              <Select value={roleKind} onValueChange={setRoleKind}>
                <SelectTrigger data-testid="role-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">First-person (self-review)</SelectItem>
                  <SelectItem value="manager">Third-person (manager review)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={run} disabled={running || !selectedIds.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="run-narrative-btn">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Generate draft
            </Button>
            {narrative && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50">
                <div className="flex items-center justify-between p-2 border-b border-slate-200">
                  <span className="text-xs text-slate-500">Draft · review-ready</span>
                  <CopyButton text={narrative} />
                </div>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap p-4 font-sans leading-relaxed">{narrative}</pre>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-export-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatMonthLabel(ym) {
  if (!ym || ym === "—") return "Undated";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleString("en", { month: "long", year: "numeric" });
}
